import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AmplitudeService } from '../../services';

interface MenuItem {
  icon: string;
  label: string;
  routerLink: string;
  children?: MenuItem[];
  isOpen?: boolean;
}

@Component({
  selector: 'app-sidebar',
  imports: [CommonModule, FontAwesomeModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit {
  @Input() isSidebarCollapsed = false;
  @Output() sidebarToggle = new EventEmitter<void>();


  menuItems: MenuItem[] = [
    {
      icon: 'fa-solid fa-dollar-sign',
      label: 'Opportunities',
      isOpen: false,
      routerLink: '/dashboard'
    },
    {
      icon: 'fa-solid fa-calendar-days',
      label: 'My Past Meetings',
      isOpen: false,
      routerLink: '/meetings'
    },
    {
      icon: 'fa-solid fa-question ',
      label: 'Add Question',
      isOpen: false,
      routerLink: '/add-question'
    },
    {
      icon: 'fa-solid fa-cloud-arrow-up',
      label: 'Upload',
      routerLink: '/upload'
    }
  ];

  userName = '';
  name = '';
  avatarUrl = '';

  constructor(
    private readonly _router: Router,
    private readonly _amplitudeService: AmplitudeService
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    // Set up interval to check for user data updates (for OAuth flow)
    const checkInterval = setInterval(() => {
      const userInfo = JSON.parse(sessionStorage.getItem('userInfo') as any);
      if (userInfo?.user?.email && this.userName === '-') {
        this.loadUserInfo();
        clearInterval(checkInterval);
      }
    }, 500);
    
    // Clear interval after 10 seconds to avoid infinite checking
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  private loadUserInfo(): void {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') as any);
    this.userName = userInfo?.user?.email ?? '-';
    this.name = userInfo?.user?.user_metadata?.full_name || userInfo?.user?.user_metadata?.name || '';
    
    // Get avatar URL from user_metadata (prioritize avatar_url, fallback to picture)
    this.avatarUrl = userInfo?.user?.user_metadata?.avatar_url || userInfo?.user?.user_metadata?.picture || '';
  }

  goToDashboard(): void {
    this._router.navigate(['/dashboard']);
  }

  logout(): void {
    // Track logout event
    this._amplitudeService.trackEvent('Logout', {
      user_id: this.getUserId(),
      source: 'SidePane'
    });
    
    sessionStorage.removeItem('userInfo');
    this._router.navigate(['/login'])
  }

  openSettings(): void {
    this._amplitudeService.trackEvent('Settings_Open', {
      user_id: this.getUserId(),
      source: 'sidebar_profile_menu'
    });

    this._router.navigate(['/settings']);
  }

  toggleSidebar() {
    // Track menu minimise
    this._amplitudeService.trackEvent('Menu_minimise', {
      user_id: this.getUserId(),
      action: 'minimise'
    });
    
    this.sidebarToggle.emit();
  }

  toggleMenuItem(item: MenuItem) {
    // Updated menu click tracking
    this._amplitudeService.trackEvent('Menu_click', {
      menu_item: item.routerLink.replace('/', ''),
      user_id: this.getUserId()
    });
    
    if (!this.isSidebarCollapsed && item.children) {
      item.isOpen = !item.isOpen;
    }
  }

  private getUserId(): string {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') as any);
    return userInfo?.user?.id || userInfo?.id || 'anonymous';
  }

  onImageError(): void {
    // If profile image fails to load, clear the URL so fallback icon remains visible
    this.avatarUrl = '';
  }
}
